import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import Breadcrumbs from '../../../components/BreadCrumbs';
import StyledHeadingText from '../../../components/Fonts/StyledHeadingText';
import SearchBar from '../../../components/searchBar/SearchBar';
import Dropdown from '../../../components/inputFields/DropDown';
import DatePicker from '../../../components/inputFields/DatePicker';
import B2CLeadDetail from '../../../components/leads/b2c/B2CLeadDetail';
import Table from '../../../components/tables/Table';
import Pagination from '../../../components/tables/pagination/Pagination';
import CustomLoading from '../../../components/customLoading/CustomLoading';
import { getB2CLeadPhoneStats, getB2CSalesPersonDashboard, updateB2CLeadStatus, downloadB2CLeads, uploadAndAssignB2CLeads, addB2CSingleLead, downloadB2CNpcLeadsSheet } from '../../../services/b2cLeads';
import { toast } from 'react-toastify';
import { BiRefresh, BiLinkExternal, BiEdit } from 'react-icons/bi';
import { BsWhatsapp, BsX, BsCheckCircleFill, BsExclamationTriangleFill } from 'react-icons/bs';
import { getUserId, getUserRole } from '../../../helpers/local_storage_helpers';
import axios from 'axios';
import TabView from '../../../components/TabView';
import { searchStaffUsers, searchProduct } from '../../../services/search';
import AsyncDropdown from '../../../components/inputFields/AsyncDropDown';
import { Download, Upload, UserPlus } from 'lucide-react';

const STATUS_CHOICES = [
    { value: 'new', label: 'New' },
    { value: 'callback', label: 'Call Back' },
    { value: 'follow_up', label: 'Follow Up' },
    { value: 'npc', label: 'NPC' },
    { value: 'not_interested', label: 'Not Interested' },
    // { value: 'blocked', label: 'Blocked' },
    { value: 'converted', label: 'Converted' },
    { value: 'retailer', label: 'Retailer' },
    { value: 'interested', label: 'Interested' },
];

const COLOR_HIGHLIGHTS = [
    { name: 'None', class: '', color: '#ffffff', bgColor: '#ffffff' },
    { name: 'Critical (Red)', class: 'bg-rose-200 font-medium', color: '#f43f5e', bgColor: '#fecdd3' },
    { name: 'Action (Blue)', class: 'bg-sky-200 font-medium', color: '#0ea5e9', bgColor: '#bae6fd' },
    { name: 'Success (Green)', class: 'bg-emerald-200 font-medium', color: '#10b981', bgColor: '#a7f3d0' },
    { name: 'Warning (Yellow)', class: 'bg-amber-200 font-medium', color: '#f59e0b', bgColor: '#fde68a' },
    { name: 'Priority (Purple)', class: 'bg-violet-200 font-medium', color: '#8b5cf6', bgColor: '#ddd6fe' },
    { name: 'Hot (Orange)', class: 'bg-orange-200 font-medium', color: '#f97316', bgColor: '#fed7aa' },
    { name: 'Fresh (Lime)', class: 'bg-lime-200 font-medium', color: '#84cc16', bgColor: '#d9f99d' },
    { name: 'Interested (Teal)', class: 'bg-teal-200 font-medium', color: '#14b8a6', bgColor: '#99f6e4' },
];

function B2CSalesPersonView() {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [stats, setStats] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [searchText, setSearchText] = useState("");
    const [selectedDate, setSelectedDate] = useState(null);
    const [statusFilter, setStatusFilter] = useState("");
    const [productFilter, setProductFilter] = useState("");
    const [selectedLeadForDetail, setSelectedLeadForDetail] = useState(null); // { id, isAssignmentId }
    const [isUpdating, setIsUpdating] = useState(null); // ID of lead being updated

    const [staffOptions, setStaffOptions] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);

    // Upload & Result States
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadUser, setUploadUser] = useState(null);
    const [uploadDate, setUploadDate] = useState(null);
    const [uploadResult, setUploadResult] = useState(null); // { success: boolean, message: string, data: any }

    // Add Single Lead States (admin only)
    const [showAddLeadModal, setShowAddLeadModal] = useState(false);
    const [addLeadSubmitting, setAddLeadSubmitting] = useState(false);
    const [addLeadResult, setAddLeadResult] = useState(null);
    const [addLeadForm, setAddLeadForm] = useState({
        contact: '',
        sales_person: '',
        name: '',
        product: '',
        created_at: new Date().toISOString().split('T')[0],
    });

    // Retailer Modal States
    const [showRetailerModal, setShowRetailerModal] = useState(false);
    const [retailerLead, setRetailerLead] = useState(null);
    const [retailerShopName, setRetailerShopName] = useState('');
    const [retailerBusinessCard, setRetailerBusinessCard] = useState(null);
    const [retailerSubmitting, setRetailerSubmitting] = useState(false);

    const [downloadingSheet, setDownloadingSheet] = useState(false);

    const pageSize = 50;
    const cancelTokenRef = useRef(null);

    const fetchStaff = async () => {
        try {
            const res = await searchStaffUsers({ query: "?admin=true" });
            if (res.status === 200) {
                setStaffOptions(res.data.map(s => ({ label: s.display_name || s.name || `${s.first_name} ${s.last_name}`, value: s.id })));
            }
        } catch (err) {
            console.error("Error fetching staff:", err);
        }
    };

    const fetchData = useCallback(async () => {
        if (cancelTokenRef.current) {
            cancelTokenRef.current.cancel();
        }
        cancelTokenRef.current = axios.CancelToken.source();

        setLoading(true);
        try {
            let query = `user_id=${selectedUser || getUserId()}&page=${currentPage}&page_size=${pageSize}`;
            if (selectedDate) {
                query += `&date=${selectedDate}`;
            }

            const response = await getB2CSalesPersonDashboard(query);
            if (response.status === 200) {
                // Assuming the response is a direct array or has a results property
                const resultData = Array.isArray(response.data) ? response.data : response.data.results || [];
                setData(resultData);
                setTotalCount(response.data.count || resultData.length);
            } else {
                toast.error("Failed to fetch data");
            }
        } catch (error) {
            if (!axios.isCancel(error)) {
                console.error("Error fetching B2C leads:", error);
                toast.error("Error fetching data");
            }
        } finally {
            setLoading(false);
        }
        try {
            const statsRes = await getB2CLeadPhoneStats(`${selectedUser || getUserId()}&date=${selectedDate || ""}`);
            if (statsRes.status === 200) {
                setStats(statsRes.data || []);
            }
        } catch (err) {
            console.error("Error fetching phone stats:", err);
        }
    }, [currentPage, selectedDate, selectedUser]);

    const handleDownload = async () => {
        const userId = selectedUser || getUserId();
        const date = selectedDate || "";

        if (!userId) {
            toast.error("Please select a staff member first");
            return;
        }

        const toastId = toast.info("Preparing download...", { autoClose: false });

        try {
            const queryParams = new URLSearchParams({
                user_id: userId,
                date: date
            });

            const res = await downloadB2CLeads(queryParams.toString());

            if (res.status === 200) {
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `b2c_assigned_leads_${userId}_${date || 'all'}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                window.URL.revokeObjectURL(url);
                toast.update(toastId, { render: "Download started!", type: "success", autoClose: 3000 });
            }
        } catch (err) {
            console.error("Error downloading leads:", err);
            toast.update(toastId, { render: "Failed to download leads", type: "error", autoClose: 3000 });
        }
    };

    const handleDownloadStatusSheet = async () => {
        if (getUserRole() !== 'admin') return;
        setDownloadingSheet(true);
        try {
            const params = new URLSearchParams();
            if (statusFilter) params.append('status', statusFilter);
            if (selectedDate) params.append('assigned_date', selectedDate);

            const res = await downloadB2CNpcLeadsSheet(params.toString());

            const disposition = res.headers?.['content-disposition'] || '';
            const match = disposition.match(/filename="?([^"]+)"?/);
            const filename = match ? match[1] : `b2c_${statusFilter || 'npc'}_leads.xlsx`;

            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            let message = 'Unable to download sheet.';
            if (err?.response?.data instanceof Blob) {
                try {
                    message = await err.response.data.text();
                } catch (_) { /* ignore */ }
            }
            console.error('Error downloading B2C leads sheet:', err);
            toast.error(message || 'Unable to download sheet.');
        } finally {
            setDownloadingSheet(false);
        }
    };

    const handleRetailerSubmit = async (e) => {
        e.preventDefault();
        setRetailerSubmitting(true);
        try {
            const formData = new FormData();
            formData.append('lead_status', 'retailer');
            if (retailerLead.name) formData.append('name', retailerLead.name);
            if (retailerLead.area) formData.append('area', retailerLead.area);
            if (retailerLead.district) formData.append('district', retailerLead.district);
            if (retailerLead.state) formData.append('state', retailerLead.state);

            formData.append('shop_name', retailerShopName.trim());
            if (retailerBusinessCard) {
                formData.append('retailer_bussiness_card', retailerBusinessCard);
            }

            const res = await updateB2CLeadStatus(retailerLead.id, formData);
            if (res.status === 200) {
                toast.success("Lead marked as Retailer successfully");
                setShowRetailerModal(false);
                setRetailerLead(null);
                setRetailerShopName('');
                setRetailerBusinessCard(null);
                fetchData();
            }
        } catch (error) {
            console.error("Retailer status change error:", error);
            toast.error("Failed to update lead status");
        } finally {
            setRetailerSubmitting(false);
        }
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!uploadFile) {
            toast.error("Please select a file");
            return;
        }
        if (!uploadUser) {
            toast.error("Please select a sales person");
            return;
        }
        if (!uploadDate) {
            toast.error("Please select an assigned date");
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', uploadFile);
            formData.append('sales_person', uploadUser);
            formData.append('assigned_date', uploadDate);

            const res = await uploadAndAssignB2CLeads(formData);
            if (res.status === 200 || res.status === 201) {
                setUploadResult({
                    success: true,
                    message: res.data?.message || "Leads uploaded and assigned successfully",
                    data: res.data
                });
                setShowUploadModal(false);
                setUploadFile(null);
                fetchData();
            }
        } catch (err) {
            console.error("Error uploading leads:", err);
            setUploadResult({
                success: false,
                message: err.response?.data?.error || err.response?.data?.message || err.message || "Failed to upload leads",
                data: err.response?.data
            });
        } finally {
            setUploading(false);
        }
    };

    const handleAddLead = async (e) => {
        e.preventDefault();
        if (!addLeadForm.contact.trim()) { toast.error('Contact number is required'); return; }
        if (!addLeadForm.sales_person) { toast.error('Please select a sales person'); return; }
        setAddLeadSubmitting(true);
        try {
            const res = await addB2CSingleLead({
                contact: addLeadForm.contact.trim(),
                sales_person: addLeadForm.sales_person,
                name: addLeadForm.name.trim() || undefined,
                product: addLeadForm.product.trim() || undefined,
                created_at: addLeadForm.created_at,
            });
            if (res.status === 200 || res.status === 201) {
                toast.success('Lead added successfully!');
                setAddLeadResult(res.data);
                setShowAddLeadModal(false);
                setAddLeadForm({ contact: '', sales_person: '', name: '', product: '', created_at: new Date().toISOString().split('T')[0] });
                fetchData();
            }
        } catch (err) {
            console.error('Add lead error:', err);
            toast.error(err.response?.data?.message || err.response?.data?.error || 'Failed to add lead');
        } finally {
            setAddLeadSubmitting(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        fetchStaff();
    }, []);

    const handleSearch = (query) => {
        setSearchText(query);
    };

    const handleDateChange = useCallback((date) => {
        setSelectedDate(date);
        setCurrentPage(1);
    }, []);

    const resetFilters = () => {
        setSearchText("");
        setSelectedDate(null);
        setStatusFilter("");
        setProductFilter("");
        setCurrentPage(1);
    };

    const handleStatusChange = async (lead, newStatus) => {
        if (newStatus === 'retailer') {
            setRetailerLead(lead);
            setRetailerShopName(lead.shop_name || '');
            setRetailerBusinessCard(null);
            setShowRetailerModal(true);
            return;
        }
        try {
            const res = await updateB2CLeadStatus(lead.id, {
                lead_status: newStatus,
                name: lead.name,
                area: lead.area,
                district: lead.district,
                state: lead.state
            });
            if (res.status === 200) {
                toast.success("Status updated");
                fetchData();
            }
        } catch (error) {
            toast.error("Failed to update status");
        }
    };

    const handleUpdateLead = async (lead, updatedFields) => {
        setIsUpdating(lead.id);
        try {
            // Map follow_up string back to start/end dates if it's being updated
            let payload = {
                ...updatedFields,
                name: updatedFields.name || lead.name,
                lead_status: lead.lead_status || lead.status, // Fallback for either field name
                area: lead.area,
                district: lead.district,
                state: lead.state
            };

            if (updatedFields.follow_up) {
                payload.follow_up_start_date = updatedFields.follow_up;
                payload.follow_up_end_date = null;
                delete payload.follow_up;
            }

            const res = await updateB2CLeadStatus(lead.id, payload);
            if (res.status === 200) {
                toast.success("Lead updated successfully");
                fetchData();
            }
        } catch (error) {
            console.error("Update error:", error);
            toast.error("Failed to update lead");
        } finally {
            setIsUpdating(null);
        }
    };

    const openWhatsapp = (phone) => {
        const url = `https://wa.me/91${phone}`;
        window.open(url, "_blank");
    };

    const openLeadDetail = (item) => {
        setSelectedLeadForDetail({
            id: item.id,
            isAssignmentId: true,
            phone: item.phone,
            user_id: item.user_id,
            is_paid: item.is_paid,
            shop_name: item.shop_name || item.lead?.shop_name,
            retailer_bussiness_card: item.retailer_bussiness_card || item.lead?.retailer_bussiness_card,
        });
    };

    const headers = [
        { label: "S No", key: "index", width: "40px" },
        { label: "Name", key: "name", width: "100px" },
        { label: "Phone", key: "phone", width: "90px" },
        { label: "Paid", key: "is_paid", width: "72px" },
        // { label: "Area", key: "area", width: "100px" },
        // { label: "S/D", key: "sd", width: "100px" },
        { label: "Product", key: "product", width: "100px" },
        { label: "Status", key: "lead_status", width: "100px" },
        { label: "Note", key: "note", width: "300px" },
        { label: "Follow Up", key: "follow_up", width: "120px" },
        { label: "WA / Pay", key: "communication_status", width: "90px" },
        // { label: "Highlight", key: "highlight", width: "120px" },
        { label: "Actions", key: "actions", width: "90px" },
    ];

    const productOptions = useMemo(() => {
        const products = Array.from(new Set(data.map(item => item.product).filter(p => p)));
        return [{ label: "All Products", value: "" }, ...products.map(p => ({ label: p, value: p }))];
    }, [data]);

    const filteredData = useMemo(() => {
        let results = data;

        if (statusFilter) {
            results = results.filter(item => item.lead_status === statusFilter);
        }

        if (productFilter) {
            results = results.filter(item => item.product === productFilter);
        }

        if (searchText) {
            const lowSearch = searchText.toLowerCase();
            results = results.filter(item =>
                (item.name && item.name.toLowerCase().includes(lowSearch)) ||
                (item.phone && item.phone.toString().toLowerCase().includes(lowSearch))
            );
        }

        return results;
    }, [data, searchText, statusFilter, productFilter]);

    const rows = filteredData.map((item, index) => ({
        index: <div className="text-center">{(currentPage - 1) * pageSize + index + 1}</div>,
        name: item.name ? (
            <button
                type="button"
                onClick={() => openLeadDetail(item)}
                className="text-primary flex flex-col items-start hover:underline font-bold text-left"
            >
                {item.name}
                <span className="text-xs text-gray-500 font-normal">Type: {item.type || "N/A"}</span>
                {item.shop_name && (
                    <span className="text-[10px] text-indigo-600 font-bold mt-0.5">Shop: {item.shop_name}</span>
                )}
            </button>
        ) : (
            <input
                key={`name-${item.id}`}
                type="text"
                className="border border-gray-300 rounded p-1 text-xs w-full max-w-full box-border placeholder:text-gray-400 placeholder:italic focus:ring-1 focus:ring-primary outline-none"
                placeholder="Enter Name"
                onBlur={(e) => {
                    if (e.target.value) {
                        handleUpdateLead(item, { name: e.target.value });
                    }
                }}
            />
        ),
        phone: item.phone ? (
            <button
                type="button"
                onClick={() => openLeadDetail(item)}
                className="font-bold text-primary hover:underline text-left"
            >
                {item.phone}
            </button>
        ) : (
            <span>—</span>
        ),
        is_paid: (
            <span
                className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap ${
                    item.is_paid ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"
                }`}
                title={
                    item.is_paid
                        ? "Customer has at least one non-pending shop order"
                        : "No non-pending shop order for this phone"
                }
            >
                {item.is_paid ? "Paid" : "Unpaid"}
            </span>
        ),
        area: item.area || "N/A",
        sd: item.sd || "N/A",
        product: item.product || "N/A",
        lead_status: (
            <div className="flex items-center gap-1.5 min-w-[120px]">
                <select
                    value={item.lead_status || ""}
                    onChange={(e) => handleStatusChange(item, e.target.value)}
                    className="text-[11px] font-bold py-1 px-2 rounded border border-gray-300 focus:ring-1 focus:ring-primary outline-none cursor-pointer w-full transition-colors"
                    style={{ backgroundColor: COLOR_HIGHLIGHTS.find(c => c.class === item.highlight_color)?.bgColor || '#ffffff' }}
                >
                    <option value="">Select</option>
                    {STATUS_CHOICES.map(choice => (
                        <option key={choice.value} value={choice.value}>{choice.label}</option>
                    ))}
                </select>
                {item.lead_status === 'retailer' && (
                    <button
                        onClick={() => handleStatusChange(item, 'retailer')}
                        className="p-1 hover:bg-gray-100 rounded-full text-indigo-600 shrink-0"
                        title="Edit Retailer Details"
                    >
                        <BiEdit size={14} />
                    </button>
                )}
            </div>
        ),
        communication_status: (
            <div className="flex justify-center items-center gap-2 h-full">
                <input
                    type="checkbox"
                    checked={item.is_whatsapp_sent}
                    onChange={(e) => handleUpdateLead(item, { is_whatsapp_sent: e.target.checked })}
                    className="cursor-pointer"
                    title="Whatsapp Sent"
                />
                <span className="text-gray-300">/</span>
                <input
                    type="checkbox"
                    checked={item.is_payment_details_sent}
                    onChange={(e) => handleUpdateLead(item, { is_payment_details_sent: e.target.checked })}
                    className="cursor-pointer"
                    title="Payment Details Sent"
                />
            </div>
        ),
        note: (
            <textarea
                key={item.id}
                className="border border-gray-300 rounded p-1 text-[10px] w-full min-w-[100px] h-8 resize-none"
                defaultValue={item.note || ""}
                onBlur={(e) => {
                    if (e.target.value !== (item.note || "")) {
                        handleUpdateLead(item, { note: e.target.value });
                    }
                }}
            />
        ),
        follow_up: (
            <div className="min-w-[110px]">
                <DatePicker
                    placeholder="Date"
                    className="scale-90 origin-left text-[11px] py-0 text-center bg-white shadow-none"
                    value={item.follow_up ? (item.follow_up.includes(" to ") ? item.follow_up.split(" to ")[0] : item.follow_up) : ""}
                    onChange={(date) => {
                        if (date !== (item.follow_up || "")) {
                            handleUpdateLead(item, { follow_up: date });
                        }
                    }}
                />
            </div>
        ),
        highlight: (
            <div className="flex bg-white/50 p-1 rounded-lg gap-1.5 justify-center border border-gray-100">
                {COLOR_HIGHLIGHTS.map((color) => (
                    <button
                        key={color.class}
                        onClick={() => handleUpdateLead(item, { highlight_color: color.class })}
                        className={`w-4 h-4 rounded-full border border-gray-200 transition-all hover:scale-125 shadow-sm ${item.highlight_color === color.class ? 'ring-2 ring-primary ring-offset-1 scale-110' : ''}`}
                        style={{ backgroundColor: color.color }}
                        title={color.name}
                    />
                ))}
            </div>
        ),
        actions: (
            <div className="flex gap-2">
                <button
                    onClick={() => handleUpdateLead(item, {})}
                    className="p-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors px-4"
                    disabled={isUpdating === item.id}
                    title="Sync/Refresh"
                >
                    <BiRefresh size={16} className={isUpdating === item.id ? "animate-spin" : ""} />
                </button>
                <button
                    onClick={() => openWhatsapp(item.phone)}
                    className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                    title="Whatsapp"
                >
                    <BsWhatsapp size={16} />
                </button>
            </div>
        ),
        rowBgColor: item.highlight_color || ""
    }));

    return (
        <div className="p-2">
            <div className="flex flex-row justify-between">
                <Breadcrumbs items={[
                    { name: 'Dashboard', link: '/dashboard' },
                    { name: 'Leads Management', link: '#' },
                    { name: 'B2C Sales Person View' },
                ]} />

                <div className="flex items-center gap-2">
                    {/* Add Single Lead — admin only */}
                    {getUserRole() === 'admin' && (
                        <button
                            title="Add Single Lead"
                            onClick={() => setShowAddLeadModal(true)}
                            className="bg-primary text-white rounded-md cursor-pointer p-2 hover:bg-primary/90 transition-all shrink-0 flex items-center justify-center"
                        >
                            <UserPlus size={20} />
                        </button>
                    )}
                    <button
                        title="Upload & Assign Leads"
                        onClick={() => {
                            setUploadUser(selectedUser || null);
                            setUploadDate(selectedDate || null);
                            setShowUploadModal(true);
                        }}
                        className='bg-emerald-600 text-white rounded-md cursor-pointer p-2 hover:bg-emerald-700 transition-all shrink-0 flex items-center justify-center'
                    >
                        <Upload size={20} />
                    </button>
                    <Download
                        className='bg-primary text-white rounded-md cursor-pointer p-2 hover:bg-primary/90 transition-all shrink-0'
                        size={36}
                        onClick={handleDownload}
                        title="Download Assigned Leads"
                    />
                    {getUserRole() === 'admin' && (
                        <button
                            title="Export Status Sheet (by Status / Assigned Date)"
                            onClick={handleDownloadStatusSheet}
                            disabled={downloadingSheet}
                            className="bg-indigo-600 text-white rounded-md cursor-pointer p-2 hover:bg-indigo-700 transition-all shrink-0 flex items-center justify-center disabled:opacity-60"
                        >
                            <Download size={20} />
                        </button>
                    )}

                </div>
            </div>

            <div className="flex justify-between items-center mb-3 mt-2 relative z-[60]">
                <StyledHeadingText text="B2C Sales Person View" />
                <div className="flex gap-4 items-center">
                    {(getUserRole() === "admin" || getUserRole() === "hr-admin") && <Dropdown
                        options={staffOptions}
                        value={selectedUser}
                        onChange={(val) => { setSelectedUser(val); setCurrentPage(1); }}
                        needSearch
                        placeholder="Staffs"
                    />}
                    <Dropdown
                        options={productOptions}
                        value={productFilter}
                        onChange={(val) => { setProductFilter(val); }}
                        placeholder="Filter Product"
                        compact
                    />
                    <SearchBar
                        placeholder="Search by name or phone..."
                        onSearch={handleSearch}
                        value={searchText}
                    />
                    <DatePicker
                        value={selectedDate}
                        onChange={handleDateChange}
                        placeholder="Filter by Date"
                        className="scale-95 origin-right"
                    />
                    {(searchText || statusFilter || selectedDate) && (
                        <button
                            onClick={resetFilters}
                            className="p-2 text-primary hover:bg-primary/10 rounded-full transition-colors"
                            title="Reset Filters"
                        >
                            <BiRefresh size={24} />
                        </button>
                    )}
                </div>
            </div>
            <div className="grid grid-flow-col auto-cols-fr gap-2 mb-2 overflow-x-auto pb-1">
                {stats.map((stat, idx) => (
                    <div key={idx} className="bg-white p-2 rounded-xl border border-gray-100 shadow-sm flex flex-col items-center justify-center text-center">
                        <div className="text-[10px] font-black text-gray-600 mb-0.5 uppercase tracking-tighter truncate w-full px-1">{stat.title}</div>
                        <div className="text-sm font-black text-primary leading-none">{stat.value}</div>
                    </div>
                ))}
            </div>

            <div className="mb-4 mt-2">
                <TabView
                    onChanged={(val) => setStatusFilter(val)}
                    isCenterAligned={false}
                    tabs={[
                        { key: "", title: "All" },
                        ...STATUS_CHOICES.map(s => ({ key: s.value, title: s.label }))
                    ].map(tab => ({
                        ...tab,
                        content: statusFilter === tab.key ? (
                            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
                                {loading && data.length === 0 ? (
                                    <div className="flex justify-center items-center py-20">
                                        <CustomLoading size={60} />
                                    </div>
                                ) : (
                                    <>
                                        <Table headers={headers} rows={rows} />
                                        {filteredData.length === 0 && (
                                            <div className="text-center py-10 text-gray-500">
                                                No leads found matching your criteria.
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        ) : null
                    }))}
                />
            </div>

            {/* Lead Detail Drawer/Modal */}
            {selectedLeadForDetail && (
                <B2CLeadDetail
                    id={selectedLeadForDetail.id}
                    isAssignmentId={selectedLeadForDetail.isAssignmentId}
                    leadContext={{
                        phone: selectedLeadForDetail.phone,
                        user_id: selectedLeadForDetail.user_id,
                        is_paid: selectedLeadForDetail.is_paid,
                        shop_name: selectedLeadForDetail.shop_name,
                        retailer_bussiness_card: selectedLeadForDetail.retailer_bussiness_card,
                    }}
                    onClose={() => setSelectedLeadForDetail(null)}
                />
            )}

            {/* Upload & Assign Modal */}
            {showUploadModal && (
                <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 text-left">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
                        <div className="flex justify-between items-center p-4 border-b">
                            <h3 className="text-lg font-bold text-gray-800">Upload & Assign Leads</h3>
                            <button
                                onClick={() => setShowUploadModal(false)}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <BsX size={24} />
                            </button>
                        </div>

                        <form onSubmit={handleUpload} className="p-6 space-y-4">
                            <div className="space-y-1">
                                <label className="block text-xs font-bold text-gray-500 uppercase">Select Excel File</label>
                                <input
                                    type="file"
                                    accept=".xlsx, .xls, .csv"
                                    onChange={(e) => setUploadFile(e.target.files[0])}
                                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20 cursor-pointer"
                                />
                                {uploadFile && <p className="text-[10px] text-gray-400 mt-1">Selected: {uploadFile.name}</p>}
                            </div>

                            <Dropdown
                                label="Sales Person"
                                options={staffOptions}
                                value={uploadUser}
                                onChange={(val) => setUploadUser(val)}
                                needSearch
                                placeholder="Select Staff"
                                compact
                            />

                            <DatePicker
                                label="Assigned Date"
                                value={uploadDate}
                                onChange={(date) => setUploadDate(date)}
                                placeholder="Select Date"
                            />

                            <div className="pt-4">
                                <button
                                    type="submit"
                                    disabled={uploading}
                                    className="w-full bg-primary text-white py-2.5 rounded-xl font-bold text-sm shadow-lg shadow-primary/30 hover:shadow-primary/50 transition-all active:scale-95 disabled:bg-gray-400 disabled:shadow-none"
                                >
                                    {uploading ? (
                                        <div className="flex items-center justify-center gap-2">
                                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                            <span>Uploading...</span>
                                        </div>
                                    ) : (
                                        "Start Upload & Assign"
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Add Single Lead Modal — admin only */}
            {showAddLeadModal && getUserRole() === 'admin' && (
                <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 text-left">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
                        {/* Header — same as Upload modal */}
                        <div className="flex justify-between items-center p-4 border-b">
                            <h3 className="text-lg font-bold text-gray-800">Add Single Lead</h3>
                            <button
                                onClick={() => setShowAddLeadModal(false)}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <BsX size={24} />
                            </button>
                        </div>

                        {/* Form — same spacing/label/input pattern as Upload modal */}
                        <form onSubmit={handleAddLead} className="p-6 space-y-4">
                            {/* Contact */}
                            <div className="space-y-1">
                                <label className="block text-xs font-bold text-gray-500 uppercase">
                                    Contact Number <span className="text-rose-500">*</span>
                                </label>
                                <input
                                    type="tel"
                                    placeholder="e.g. 9983361815"
                                    maxLength={15}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-primary outline-none"
                                    value={addLeadForm.contact}
                                    onChange={(e) => setAddLeadForm(p => ({ ...p, contact: e.target.value.replace(/\D/g, '') }))}
                                />
                            </div>

                            {/* Sales Person */}
                            <Dropdown
                                label="Sales Person"
                                options={staffOptions}
                                value={addLeadForm.sales_person}
                                onChange={(val) => setAddLeadForm(p => ({ ...p, sales_person: val }))}
                                needSearch
                                placeholder="Select Staff"
                                compact
                            />

                            {/* Name */}
                            <div className="space-y-1">
                                <label className="block text-xs font-bold text-gray-500 uppercase">
                                    Name <span className="text-gray-400 font-normal normal-case">(optional)</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="John Doe"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-primary outline-none"
                                    value={addLeadForm.name}
                                    onChange={(e) => setAddLeadForm(p => ({ ...p, name: e.target.value }))}
                                />
                            </div>

                            {/* Product Interest */}
                            <div className="space-y-1">
                                <label className="block text-xs font-bold text-gray-500 uppercase">
                                    Product Interest <span className="text-gray-400 font-normal normal-case">(optional)</span>
                                </label>
                                <AsyncDropdown
                                    key={addLeadForm.product || 'empty'}
                                    apiCallFunction={async (input) => {
                                        const response = await searchProduct(input);
                                        const results = response.data.results || response.data;
                                        return (Array.isArray(results) ? results : []).map((pro) => ({
                                            value: pro.id,
                                            label: pro.aka_name ?? pro.product_name,
                                            product_name: pro.product_name,
                                        }));
                                    }}
                                    needSearch
                                    placeholder="Search product..."
                                    onChange={(selectedOption) => {
                                        setAddLeadForm(p => ({ ...p, product: selectedOption ? selectedOption.product_name : '' }));
                                    }}
                                />
                            </div>

                            {/* Date */}
                            <DatePicker
                                label="Lead Date"
                                value={addLeadForm.created_at}
                                onChange={(date) => setAddLeadForm(p => ({ ...p, created_at: date }))}
                                placeholder="Select Date"
                            />

                            {/* Submit */}
                            <div className="pt-4">
                                <button
                                    type="submit"
                                    disabled={addLeadSubmitting}
                                    className="w-full bg-primary text-white py-2.5 rounded-xl font-bold text-sm shadow-lg shadow-primary/30 hover:shadow-primary/50 transition-all active:scale-95 disabled:bg-gray-400 disabled:shadow-none"
                                >
                                    {addLeadSubmitting ? (
                                        <div className="flex items-center justify-center gap-2">
                                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                            <span>Adding Lead...</span>
                                        </div>
                                    ) : (
                                        'Add Lead'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Add Single Lead Result Popup — admin only */}
            {addLeadResult && (
                <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 text-left">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-300">
                        <div className="p-6 flex flex-col items-center text-center bg-emerald-50">
                            <BsCheckCircleFill className="text-emerald-500 text-5xl mb-4" />
                            <h3 className="text-xl font-black mb-1 text-emerald-800">
                                Lead Added Successfully
                            </h3>
                            <p className="text-sm font-medium text-emerald-600">
                                Contact: {addLeadResult.phone || 'N/A'}
                            </p>
                        </div>

                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                {/* Lead Status */}
                                <div className={`p-3 rounded-xl border ${addLeadResult.lead_created ? 'bg-emerald-50/50 border-emerald-100/50' : 'bg-blue-50/50 border-blue-100/50'}`}>
                                    <div className={`text-[10px] font-bold uppercase tracking-wider ${addLeadResult.lead_created ? 'text-emerald-500' : 'text-blue-400'}`}>Lead Status</div>
                                    <div className={`text-sm font-black ${addLeadResult.lead_created ? 'text-emerald-700' : 'text-blue-700'}`}>
                                        {addLeadResult.lead_created ? "Created" : "Existed"}
                                    </div>
                                    {addLeadResult.lead_id && (
                                        <div className="text-[9px] text-gray-400 mt-1 truncate" title={addLeadResult.lead_id}>ID: {addLeadResult.lead_id}</div>
                                    )}
                                </div>

                                {/* Assignment Status */}
                                <div className={`p-3 rounded-xl border ${addLeadResult.assignment_created ? 'bg-emerald-50/50 border-emerald-100/50' : 'bg-amber-50/50 border-amber-100/50'}`}>
                                    <div className={`text-[10px] font-bold uppercase tracking-wider ${addLeadResult.assignment_created ? 'text-emerald-500' : 'text-amber-500'}`}>Assignment</div>
                                    <div className={`text-sm font-black ${addLeadResult.assignment_created ? 'text-emerald-700' : 'text-amber-700'}`}>
                                        {addLeadResult.assignment_created ? "Assigned" : "Not Assigned"}
                                    </div>
                                    {addLeadResult.assignment_id && (
                                        <div className="text-[9px] text-gray-400 mt-1 truncate" title={addLeadResult.assignment_id}>ID: {addLeadResult.assignment_id}</div>
                                    )}
                                </div>

                                {/* Product Interest */}
                                <div className={`p-3 rounded-xl border col-span-2 ${addLeadResult.product_not_found ? 'bg-rose-50/50 border-rose-100/50' : addLeadResult.product_interest_id ? 'bg-emerald-50/50 border-emerald-100/50' : 'bg-gray-50 border-gray-100'}`}>
                                    <div className={`text-[10px] font-bold uppercase tracking-wider ${addLeadResult.product_not_found ? 'text-rose-500' : addLeadResult.product_interest_id ? 'text-emerald-500' : 'text-gray-500'}`}>Product Interest</div>
                                    <div className="text-sm font-black text-gray-700">
                                        {addLeadResult.product_not_found 
                                            ? "Product Not Found" 
                                            : addLeadResult.product_interest_id 
                                                ? "Matched & Linked" 
                                                : "No Product Sent"}
                                    </div>
                                    {addLeadResult.product_interest_id && (
                                        <div className="text-[9px] text-gray-400 mt-1 truncate" title={addLeadResult.product_interest_id}>ID: {addLeadResult.product_interest_id}</div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="p-4 border-t bg-gray-50 flex justify-center">
                            <button
                                onClick={() => setAddLeadResult(null)}
                                className="px-10 py-2.5 bg-gray-800 text-white rounded-xl font-bold text-sm hover:bg-black transition-all active:scale-95 shadow-lg shadow-gray-200"
                            >
                                Close Result
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Result Popup */}
            {uploadResult && (
                <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-300">
                        <div className={`p-6 flex flex-col items-center text-center ${uploadResult.success ? 'bg-emerald-50' : 'bg-rose-50'}`}>
                            {uploadResult.success ? (
                                <BsCheckCircleFill className="text-emerald-500 text-5xl mb-4" />
                            ) : (
                                <BsExclamationTriangleFill className="text-rose-500 text-5xl mb-4" />
                            )}
                            <h3 className={`text-xl font-black mb-1 ${uploadResult.success ? 'text-emerald-800' : 'text-rose-800'}`}>
                                {uploadResult.success ? "Success!" : "Upload Failed"}
                            </h3>
                            <p className={`text-sm font-medium ${uploadResult.success ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {uploadResult.message}
                            </p>
                        </div>

                        {uploadResult.data && (
                            <div className="p-6 space-y-4">
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="p-3 bg-blue-50/50 rounded-xl border border-blue-100/50">
                                        <div className="text-[10px] font-bold text-blue-400 uppercase tracking-wider">New Leads</div>
                                        <div className="text-xl font-black text-blue-700">{uploadResult.data.created_leads ?? 0}</div>
                                    </div>
                                    <div className="p-3 bg-indigo-50/50 rounded-xl border border-indigo-100/50">
                                        <div className="text-[10px] font-bold text-indigo-400 uppercase tracking-wider">Updated</div>
                                        <div className="text-xl font-black text-indigo-700">{uploadResult.data.updated_leads ?? 0}</div>
                                    </div>
                                    <div className="p-3 bg-emerald-50/50 rounded-xl border border-emerald-100/50">
                                        <div className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider">Total Assigned</div>
                                        <div className="text-xl font-black text-emerald-700">{uploadResult.data.assigned ?? 0}</div>
                                    </div>
                                    <div className="p-3 bg-amber-50/50 rounded-xl border border-amber-100/50">
                                        <div className="text-[10px] font-bold text-amber-500 uppercase tracking-wider">Alt Assigned</div>
                                        <div className="text-xl font-black text-amber-600">{uploadResult.data.skipped_already_assigned ?? 0}</div>
                                    </div>
                                    <div className="p-3 bg-rose-50/50 rounded-xl border border-rose-100/50 col-span-2">
                                        <div className="text-[10px] font-bold text-rose-400 uppercase tracking-wider">Invalid / Failed Rows</div>
                                        <div className="text-xl font-black text-rose-700">{uploadResult.data.skipped_invalid_rows ?? 0}</div>
                                    </div>
                                </div>

                                <div className="flex items-center justify-between px-2 pt-2 border-t border-gray-100">
                                    <div className="flex flex-col">
                                        <span className="text-[9px] font-bold text-gray-400 uppercase">Assigned Date</span>
                                        <span className="text-xs font-black text-gray-700">{uploadResult.data.assigned_date || 'N/A'}</span>
                                    </div>
                                    <div className="flex flex-col items-end">
                                        <span className="text-[9px] font-bold text-gray-400 uppercase">Target Staff ID</span>
                                        <span className="text-xs font-black text-gray-700">#{uploadResult.data.sales_person || 'N/A'}</span>
                                    </div>
                                </div>

                                {uploadResult.data.errors && uploadResult.data.errors.length > 0 && (
                                    <div className="mt-4">
                                        <label className="text-[10px] font-bold text-gray-400 uppercase mb-1 block">Error Log</label>
                                        <div className="max-h-32 overflow-y-auto bg-gray-50 rounded-lg p-2 border border-gray-100 italic text-[11px] text-gray-600 space-y-1">
                                            {uploadResult.data.errors.map((err, i) => (
                                                <div key={i} className="flex gap-2">
                                                    <span className="font-bold text-rose-400">•</span>
                                                    <span>{typeof err === 'string' ? err : JSON.stringify(err)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="p-4 border-t bg-gray-50 flex justify-center">
                            <button
                                onClick={() => setUploadResult(null)}
                                className="px-10 py-2.5 bg-gray-800 text-white rounded-xl font-bold text-sm hover:bg-black transition-all active:scale-95 shadow-lg shadow-gray-200"
                            >
                                Close Result
                            </button>
                        </div>
                    </div>
                </div>
            )}
              {/* Retailer Info Modal */}
            {showRetailerModal && (
                <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 text-left">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-visible animate-in fade-in zoom-in duration-200">
                        <div className="flex justify-between items-center p-4 border-b">
                            <h3 className="text-lg font-bold text-gray-800">Mark Lead as Retailer</h3>
                            <button
                                onClick={() => {
                                    setShowRetailerModal(false);
                                    setRetailerLead(null);
                                }}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <BsX size={24} />
                            </button>
                        </div>

                        <form onSubmit={handleRetailerSubmit} className="p-6 space-y-4">
                            {/* Shop Name */}
                            <div className="space-y-1">
                                <label className="block text-xs font-bold text-gray-500 uppercase">
                                    Shop/Business Name <span className="text-gray-400 font-normal normal-case">(optional)</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Enter shop name"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-primary outline-none"
                                    value={retailerShopName}
                                    onChange={(e) => setRetailerShopName(e.target.value)}
                                />
                            </div>

                            {/* Business Card Upload */}
                            <div className="space-y-1">
                                <label className="block text-xs font-bold text-gray-500 uppercase">
                                    Retailer Business Card <span className="text-gray-400 font-normal normal-case">(optional)</span>
                                </label>
                                {retailerLead?.retailer_bussiness_card && (
                                    <div className="mb-2">
                                        <p className="text-[10px] text-gray-400 font-semibold mb-1">Current Card:</p>
                                        <a
                                            href={retailerLead.retailer_bussiness_card}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-block"
                                        >
                                            <img
                                                src={retailerLead.retailer_bussiness_card}
                                                alt="Retailer Business Card"
                                                className="max-h-24 rounded-lg border object-cover hover:opacity-80 transition-opacity"
                                            />
                                        </a>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    accept="image/*"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-primary outline-none"
                                    onChange={(e) => setRetailerBusinessCard(e.target.files[0] || null)}
                                />
                            </div>

                            {/* Submit */}
                            <div className="pt-4">
                                <button
                                    type="submit"
                                    disabled={retailerSubmitting}
                                    className="w-full bg-primary text-white py-2.5 rounded-xl font-bold text-sm shadow-lg shadow-primary/30 hover:shadow-primary/50 transition-all active:scale-95 disabled:bg-gray-400 disabled:shadow-none"
                                >
                                    {retailerSubmitting ? (
                                        <div className="flex items-center justify-center gap-2">
                                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                            <span>Submitting...</span>
                                        </div>
                                    ) : (
                                        'Mark as Retailer'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default B2CSalesPersonView;
